import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGa from 'react-ga4';
import { MeInfo } from 'interfaces/auth';
import { StoreType } from 'interfaces/store';
import { getStoreAdmin, getMyInfo, setMyInfo } from 'utils/storages';
import { useStore } from 'hooks';

interface MeInfoContextInterface {
  meInfo: MeInfo | undefined;
  selectedStore: StoreType | undefined;
  changeMeInfo: (v: MeInfo) => void;
  changeStore: (v) => void;
}
export const MeInfoContext = React.createContext<MeInfoContextInterface>({
  meInfo: undefined,
  selectedStore: undefined,
  changeMeInfo: () => {},
  changeStore: () => {}
});

export const MeInfoProvider = ({ children }) => {
  const initStore = getMyInfo();
  const [meInfo, setMeInfo] = useState<MeInfo | undefined>(() => {
    const storeAdmin = getStoreAdmin();
    return storeAdmin ? { admin: storeAdmin } : undefined;
  });
  const [selectedStore, setSelectedStore] = useState<StoreType>(initStore);
  const [sid, setSid] = useState<string>(initStore?.sid || '');
  const { data } = useStore(sid, !!sid);

  useEffect(() => {
    if (data) {
      const { sid: key } = data;
      const currentStore = meInfo?.storeList?.filter(({ sid: id }) => id === key);
      const newData = { ...data };
      newData.meInfoCurrent = currentStore?.length === 1 ? currentStore[0] : undefined;
      setSelectedStore(newData);
    }
  }, [meInfo, data]);

  // 이후 모든 GA 이벤트에 계정·매장을 사용자 속성으로 싣는다 - 매장은 고른 경우만 (안 고른 상위계정 행동이 첫 매장으로 잡히지 않게)
  const storeAccountId = meInfo?.admin?.said ?? null;
  const storeId = selectedStore?.sid ?? null;
  useEffect(() => {
    ReactGa.gtag('set', 'user_properties', { store_account_id: storeAccountId, store_id: storeId });
    // 로그아웃하면 이 provider가 내려간다 - null이어야 해제된다 (undefined는 아예 안 보내서 앞 값이 남는다)
    return () => ReactGa.gtag('set', 'user_properties', { store_account_id: null, store_id: null });
  }, [storeAccountId, storeId]);

  const changeMeInfo = useCallback((me: MeInfo) => {
    if (me.storeList && me.storeList.length === 1) {
      const store = me.storeList[0];
      setMyInfo(store); // for legacy code
      setSid(store.sid); // store search trigger
    }
    setMeInfo(me);
  }, []);

  const changeStore = useCallback((store) => {
    setSelectedStore(store);
    setMyInfo(store); // for legacy code
    setSid(store?.sid);
  }, []);
  const value = useMemo(
    () => ({ meInfo, selectedStore, changeStore, changeMeInfo }),
    [meInfo, selectedStore, changeStore, changeMeInfo]
  );

  return <MeInfoContext.Provider value={value}>{children}</MeInfoContext.Provider>;
};

export default MeInfoProvider;
